import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import RoomActionDrawer from "@/pages/inventory/RoomActionDrawer";
import { TimelineToolbar } from "./TimelineToolbar";
import { TimelineLegend, TimelineSummary } from "./TimelineLegend";
import { TimelineGrid } from "./TimelineGrid";
import { TimelineTooltip } from "./TimelineTooltip";
import { MoveDialog } from "./MoveDialog";
import { UnassignedTray } from "./UnassignedTray";
import { useAuth } from "@/contexts/AuthContext";
import { addDays, centredStart, daysBetween, today } from "./timelineUtils";

/** Room Inventory → Timeline View (tape chart). */
export default function RoomTimeline({ hotelId, initialDateFrom, initialDateTo, floors = [], roomTypes = [], onChanged }) {
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";
  const navigate = useNavigate();
  const [rangeSize, setRangeSize] = useState(isMobile ? 7 : 14);
  const [active, setActive] = useState("today"); // "today" | 7 | 14 | 30 — which selector button is highlighted
  const [dateFrom, setDateFrom] = useState(initialDateFrom || centredStart(today(), isMobile ? 7 : 14));
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tip, setTip] = useState(null);
  const [drawer, setDrawer] = useState(null); // { roomId, date, focus }
  const { hasPermission } = useAuth();
  const [dragActive, setDragActive] = useState(null); // { seg, room, offset }
  const [dropTarget, setDropTarget] = useState(null); // { roomId, dates[], valid, arrival, departure }
  const [move, setMove] = useState(null);
  const [moving, setMoving] = useState(false);
  const gridRef = useRef(null);
  const touchX = useRef(null);
  const hideTimer = useRef(null);
  const navigated = useRef(false);
  const size = isMobile ? 7 : rangeSize;
  const dateTo = initialDateTo && dateFrom === initialDateFrom ? initialDateTo : addDays(dateFrom, size - 1);
  // Hotel-timezone business date from the API is the source of truth for "today"; browser date only until first load.
  const hotelToday = data?.date_range?.today || today();
  const todayStart = centredStart(hotelToday, size);
  const isToday = dateFrom === todayStart;

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, ...(hotelId ? { hotel_id: hotelId } : {}), ...(fresh ? { fresh: 1 } : {}) };
      if (filter.startsWith("floor:")) params.floor = filter.slice(6);
      if (filter.startsWith("type:")) params.room_type = filter.slice(5);
      const { data: d } = await api.get("/rooms/timeline", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [dateFrom, dateTo, filter, hotelId]);
  useEffect(() => { load(); }, [load]);

  const centreTodayColumn = useCallback((smooth = false) => {
    const el = gridRef.current;
    if (!el || !data) return false;
    const idx = data.date_range.dates.indexOf(data.date_range.today);
    if (idx < 0) return false;
    const col = isMobile ? 44 : 56, roomCol = isMobile ? 90 : 180;
    const left = Math.max(0, Math.round((idx + 0.5) * col - (el.clientWidth - roomCol) / 2));
    el.scrollTo({ left, behavior: smooth ? "smooth" : "auto" });
    return true;
  }, [data, isMobile]);
  useEffect(() => { centreTodayColumn(); }, [centreTodayColumn]);

  useEffect(() => {
    // first load used the browser date; re-anchor once the hotel business date is known
    if (!data || navigated.current || initialDateFrom) return;
    const start = centredStart(data.date_range.today, size);
    if (start !== dateFrom) setDateFrom(start);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayColumnCentred = () => {
    const el = gridRef.current, colEl = el?.querySelector("[data-today]");
    if (!el || !colEl) return false;
    const g = el.getBoundingClientRect(), c = colEl.getBoundingClientRect(), roomCol = isMobile ? 90 : 180;
    const visible = c.left >= g.left + roomCol - 1 && c.right <= g.right + 1;
    if (el.scrollWidth <= el.clientWidth + 1) return visible;
    const viewportCentre = g.left + roomCol + (g.width - roomCol) / 2;
    return visible && Math.abs((c.left + c.right) / 2 - viewportCentre) <= c.width;
  };
  const shift = (dir) => { setTip(null); navigated.current = true; setActive((a) => (a === "today" ? size : a)); setDateFrom((f) => addDays(f, dir * size)); };
  const goToday = () => {
    setTip(null);
    navigated.current = true;
    setActive("today");
    if (dateFrom !== todayStart) { setDateFrom(todayStart); return; }
    const centred = data && data.date_range.dates.includes(data.date_range.today) && todayColumnCentred();
    if (centred) { load(true); toast.info("Already showing today", { duration: 1500 }); }
    else if (!centreTodayColumn(true)) load(true);
  };
  const onRange = (n) => {
    setTip(null);
    setActive(n);
    setRangeSize(n);
    if (isToday) setDateFrom(centredStart(hotelToday, n));
  };

  const showTip = (kind, room, seg, e) => {
    if (isMobile || dragActive) return;
    clearTimeout(hideTimer.current);
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ kind, room, seg: kind === "vacant" ? null : seg, date: seg?.date, x: r.left + r.width / 2, y: r.top });
  };

  // ---- drag & drop (desktop only) ----
  const canDrag = (seg) => !isMobile && ((seg.type === "occupied" && seg.stay_status === "checked_in" && hasPermission("stays.change_room")) || (seg.type === "reserved" && hasPermission("reservations.edit")));
  const spanFor = (seg, room, date) => {
    if (seg.type === "occupied") return { arrival: seg.check_in, departure: seg.check_out };
    const nights = Math.max(1, daysBetween(seg.check_in, seg.check_out));
    const arrival = addDays(date, -(dragActive?.offset || 0));
    return { arrival, departure: addDays(arrival, nights) };
  };
  const isFree = (room, seg, arrival, departure) => !room.segments.some((x) => {
    if (x === seg || (x.stay_id && x.stay_id === seg.stay_id) || (x.reservation_id && x.reservation_id === seg.reservation_id) || x.type === "vacant_dirty") return false;
    const xa = x.check_in || x.date_from, xd = x.check_out || addDays(x.date_to, 1);
    return xa < departure && arrival < xd;
  });
  const drag = {
    canDrag, active: dragActive, target: dropTarget,
    start: (seg, room, offset, e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", seg.stay_id || seg.reservation_id || "x"); setTip(null); setDragActive({ seg, room, offset }); },
    end: () => { setDragActive(null); setDropTarget(null); },
    over: (room, date) => {
      if (!dragActive) return;
      const { seg } = dragActive;
      const { arrival, departure } = spanFor(seg, room, date);
      const dates = data.date_range.dates.filter((d) => d >= arrival && d < departure);
      const sameRoom = room.room_id === dragActive.room.room_id;
      const valid = seg.type === "occupied" ? !sameRoom && isFree(room, seg, arrival, departure) : (!sameRoom || arrival !== seg.check_in) && isFree(room, seg, arrival, departure);
      if (dropTarget?.roomId !== room.room_id || dropTarget?.arrival !== arrival) setDropTarget({ roomId: room.room_id, dates: dates.length ? dates : [date], valid, arrival, departure });
    },
    drop: (room, date) => {
      if (!dragActive) return;
      const { seg, room: fromRoom } = dragActive;
      const { arrival, departure } = spanFor(seg, room, date);
      const valid = dropTarget?.valid ?? isFree(room, seg, arrival, departure);
      setDragActive(null); setDropTarget(null);
      if (!valid) { toast.error(room.room_id === fromRoom.room_id && seg.type === "occupied" ? "Drop the guest on a different room to move them" : `Room ${room.room_number} is not free for those dates`); return; }
      setMove({ kind: seg.type === "occupied" ? "stay" : "reservation", seg, fromRoom, toRoom: room, newArrival: arrival, newDeparture: departure });
    },
  };
  const confirmMove = async (reason) => {
    setMoving(true);
    try {
      const body = { kind: move.kind, id: move.kind === "stay" ? move.seg.stay_id : move.seg.reservation_id, new_room_id: move.toRoom.room_id, reason: reason || null,
        ...(move.kind === "reservation" ? { new_arrival: move.newArrival, new_departure: move.newDeparture } : {}), ...(hotelId ? { hotel_id: hotelId } : {}) };
      await api.post("/rooms/timeline/move", body);
      toast.success(move.kind === "stay" ? `${move.seg.guest_name || "Guest"} moved to Room ${move.toRoom.room_number}` : `Reservation moved to Room ${move.toRoom.room_number}`);
      setMove(null); load(true); onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.reason || formatApiError(e));
    }
    setMoving(false);
  };
  const hideTip = () => { hideTimer.current = setTimeout(() => setTip(null), 120); };
  const keepTip = () => clearTimeout(hideTimer.current);

  const openDrawer = (room, extra = {}) => { setTip(null); setDrawer({ roomId: room.room_id, ...extra }); };
  const onClick = (kind, room, seg) => {
    if (kind === "vacant") openDrawer(room, { date: seg.date, focus: "quick_actions" });
    else if (kind === "occupied") openDrawer(room, { focus: "stay", stay: seg });
    else if (kind === "reserved") openDrawer(room, { focus: "reservation" });
    else if (kind === "blocked" || kind === "out_of_order") openDrawer(room, { focus: "block" });
    else openDrawer(room, { focus: "housekeeping" });
  };
  const onTipAction = (action, t) => {
    const { room, seg, date } = t;
    if (action === "walk_in") openDrawer(room, { date, focus: "walk_in" });
    else if (action === "new_reservation") navigate(`/pms/reservations?new=1&room_id=${room.room_id}&arrival=${date}&departure=${addDays(date, 1)}`);
    else if (action === "view_stay" && seg?.stay_id) navigate(`/pms/stays/${seg.stay_id}`);
    else if (action === "view_reservation" && seg?.reservation_id) navigate(`/pms/reservations/${seg.reservation_id}`);
    else openDrawer(room, { focus: action });
  };

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (!isMobile || touchX.current == null) return;
    const el = gridRef.current, dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 70) return;
    const atEnd = el && el.scrollLeft + el.clientWidth >= el.scrollWidth - 4, atStart = el && el.scrollLeft <= 4;
    if (dx < 0 && atEnd) shift(1); else if (dx > 0 && atStart) shift(-1);
  };

  const refresh = () => { load(true); onChanged?.(); };
  const tipNode = useMemo(() => tip && (
    <div onMouseEnter={keepTip} onMouseLeave={hideTip}><TimelineTooltip tip={tip} onAction={onTipAction} /></div>
  ), [tip]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3" data-testid="room-timeline">
      <TimelineToolbar dateFrom={dateFrom} dateTo={dateTo} rangeSize={size} onShift={shift} onToday={goToday} onRange={onRange} isToday={active === "today" && isToday} activeRange={active}
        filter={filter} onFilter={(v) => { setTip(null); setFilter(v); }} floors={floors} roomTypes={roomTypes} isMobile={isMobile} />
      <div className="bg-white border border-[#D5E6E8] rounded-lg overflow-hidden relative" data-testid="timeline-container">
        <TimelineLegend />
        {data && <UnassignedTray items={data.unassigned || []} onAssigned={refresh} />}
        {loading && !data && <div className="p-10 flex items-center justify-center gap-2 text-[13px] text-[#3F5257]" data-testid="timeline-loading"><Loader2 size={16} className="animate-spin" />Loading timeline…</div>}
        {data && <TimelineGrid data={data} isMobile={isMobile} hover={tip} onHover={showTip} onLeave={hideTip} onClick={onClick} gridRef={gridRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} drag={drag} />}
        {loading && data && <div className="absolute top-12 right-3 text-[11px] text-[#97A6A9] inline-flex items-center gap-1" data-testid="timeline-refreshing"><Loader2 size={12} className="animate-spin" />updating</div>}
        <TimelineSummary summary={data?.summary} />
      </div>
      {tipNode}
      <MoveDialog move={move} busy={moving} onConfirm={confirmMove} onCancel={() => setMove(null)} />
      <RoomActionDrawer roomId={drawer?.roomId || null} date={drawer?.date} focus={drawer?.focus} stay={drawer?.stay || null} onClose={() => setDrawer(null)} onChanged={refresh} />
    </div>
  );
}
